import { Button, Divider, Tab, Tabs, } from "@mui/material"
import { TabContext, TabPanel } from "@mui/lab"
import { push } from "connected-react-router"
import { CreateBase, ListContextProvider, useRefresh } from "ra-core"
import { FC, useCallback, useState } from "react"
import { useEffect } from "react"
import { useDispatch } from "react-redux"
import { BackupsTable, ValidationsTable } from "~/components/backups/BackupsTable"
import { Content } from "~/components/content/Content"
import { OdResourcePageProps } from "~/types/resource"
import { parse, pickQueries } from '~/utils/queryStringWrapper'
import PlusIcon from '~/icons/Plus';
import CreateBackup from "~/components/backups/CreateBackup"
import LogViewer, { LogViewerProvider, useLogViewerController } from "~/components/backups/LogViewer"
import useLocalListController from "~/components/list/useLocalListController"
import { BackupListFilterContextProvider } from "~/components/backups/useBackupListFilters"
import { DatabaseResponseDto } from "~/gen/api/backup"
import { RecPatch } from "~/dataProvider/tools"
import { genUrlWithHashid } from "~/utils/hashid"
import { useHasPermit } from "~/authProvider/useHasPermit"
import { MANAGE_BACKUP_JOBS } from "~/constants"
import { nonSqlDbTypes } from "~/components/obfuscation/defs"


const BackupValidationList: FC<OdResourcePageProps> = (props) => {
  return (
    <CreateBase {...props} mutationMode={'pessimistic'} >
      <BackupValidationListCater {...props} />
    </CreateBase>
  )
}


const BackupValidationListCater: FC<OdResourcePageProps> = (props) => {
  const dispatch = useDispatch()

  const openDetail = useCallback((database: DatabaseResponseDto | undefined) => {
    if (database) {
      const detailUrl =
        nonSqlDbTypes.has(database.databaseType!)
          ? `/databases/$0/show`
          : `/backups-n-validations/list/$0/show`

      dispatch(push(
        genUrlWithHashid(detailUrl, database.databaseId || 0, database.name)))
    } 
  }, [dispatch])

  const logCtrl = useLogViewerController()
  return (
    <LogViewerProvider value={logCtrl}>
      <BackupValidationListBody onOpenDetail={openDetail} {...props} />
    </LogViewerProvider>
  )
}

export interface BackupValidationListBodyProps extends OdResourcePageProps {
  onOpenDetail: (x: DatabaseResponseDto | undefined) => void;
}

const databaseListOptions : {sorters: Record<string, (a:DatabaseResponseDto & RecPatch, b:DatabaseResponseDto & RecPatch) => number>} = {
  sorters: {
    id: (a, b) => a.id < b.id ? -1 : 1,
    name: (a, b) => (a.name || '') < (b.name || '') ? -1 : 1,
    sizeText: (a, b) => (a.size || 0) < (b.size || 0) ? -1 : 1,
    lastBackupTime: (a, b) => (a.lastBackupTime || '') < (b.lastBackupTime || '') ? -1 : 1,
    lastValidationTime: (a, b) => (a.lastValidationTime || '') < (b.lastValidationTime || '') ? -1 : 1,
  }
}

export const BackupValidationListBody: FC<BackupValidationListBodyProps> = ({
  onOpenDetail,
  location,
  options,
}) => {
  const hasWritePermit = useHasPermit([MANAGE_BACKUP_JOBS])
  const backupsCtrl = useLocalListController({ resource: 'databases', basePath: '' }, databaseListOptions)
  const validatesCtrl = useLocalListController({ resource: 'databases', basePath: '' }, databaseListOptions)
  const refresh = useRefresh()
  useCallback(() => {
    refresh()
  }, [refresh])
  
  const [currentTab, setCurrentTab] = useState('backup');
  useEffect(() => {
    const tabQuery = pickQueries(parse(location?.search || ''))('tab')
    if (!tabQuery || tabQuery === 'backup') {
      setCurrentTab('backup')
    } else {
      setCurrentTab('validation')
      
    }
  }, [location])

  const handleTabsChange = (event: any, newValue: string) => {
    setCurrentTab(newValue);
  };

  const [openCreate, setOpenCreate] = useState(false)

  return (
    <Content 
      pageName="Backup and Validation Jobs" 
      resourceName="Backup & Validation Jobs" 
      breadcrumbs={options?.breadcrumbs}
      mainActions={
        hasWritePermit &&
        <>
          {currentTab !== 'backup' ? null :
            <Button
              color="primary"
              startIcon={<PlusIcon fontSize="small" />}
              variant="contained"
              onClick={() => setOpenCreate(true)}
            >
              Create Backup Job
            </Button>}
          <CreateBackup open={openCreate} setOpen={setOpenCreate} />
        </>
      }
    >
      <TabContext value={currentTab}>
        <Tabs
          indicatorColor="primary"
          onChange={handleTabsChange}
          scrollButtons="auto"
          textColor="primary"
          value={currentTab}
          variant="scrollable"
          >
          <Tab label="Backup Jobs" value="backup" />
          <Tab label="Validation Jobs" value="validation" />
        </Tabs>
        <Divider />
        <ListContextProvider value={backupsCtrl}>
          <BackupListFilterContextProvider flavor="backups">
            <TabPanel value="backup" sx={{ px: 0 }}>
              <BackupsTable onOpenDetail={onOpenDetail} />
            </TabPanel>
          </BackupListFilterContextProvider>
        </ListContextProvider>
        <ListContextProvider value={validatesCtrl}>
          <BackupListFilterContextProvider flavor="validates">
            <TabPanel value="validation" sx={{ px: 0 }}>
              <ValidationsTable onOpenDetail={onOpenDetail} />
            </TabPanel>
          </BackupListFilterContextProvider>
        </ListContextProvider>
      </TabContext>
      <LogViewer />
    </Content>
  )
}

export default BackupValidationList;


